import React from 'react';
import { Phone, BookOpen, AlertTriangle, CheckCircle, Package, Shield } from 'lucide-react';
export function CargoSecurementRulesPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-4926">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-4927">
        <div className="container mx-auto px-4" data-id="element-4928">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-4929">
            FMCSA Cargo Securement Rules
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-4930">
            Page updated: March 22, 2024
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-4931">
        <div className="max-w-4xl mx-auto" data-id="element-4932">
          {/* Introduction */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4933">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-4934">
              <BookOpen className="mr-3 text-[#B1050B]" size={28} data-id="element-4935" />
              Overview
            </h2>

            <p className="text-lg text-gray-700 mb-4" data-id="element-4936">
              The Federal Motor Carrier Safety Administration (FMCSA)
              implemented new cargo securement rules on{' '}
              <strong data-id="element-4937">January 1, 2004</strong>, derived from the North American
              Cargo Securement Standard Model Regulations.
            </p>

            <p className="text-gray-700 mb-4" data-id="element-4938">
              These changes arose from extensive research and consultations
              across the U.S. and Canada, aiming to harmonize cargo securement
              regulations across North America, including Mexico, as much as
              possible.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-4939">
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-4940">
                Primary Objective
              </h3>
              <p className="text-gray-700" data-id="element-4941">
                Enhance road safety by minimizing accidents caused by cargo
                shifting or falling from commercial vehicles in interstate
                commerce.
              </p>
            </div>
          </div>

          {/* Applicability */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4942">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4943">
              Applicability of the New Rules
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-4944">
              The new cargo securement rules apply to the same types of vehicles
              and cargo as the old rules, covering all cargo-carrying commercial
              motor vehicles (as defined in 49 CFR 390.5) operated in interstate
              commerce.
            </p>

            <div className="bg-green-50 rounded-lg p-6 mb-4" data-id="element-4945">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-4946">✅ Applies To:</h3>
              <p className="text-gray-700" data-id="element-4947">All types of articles of cargo</p>
            </div>

            <div className="bg-red-50 rounded-lg p-6" data-id="element-4948">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-4949">❌ Exceptions:</h3>
              <p className="text-gray-700" data-id="element-4950">
                Commodities in bulk that lack structure or fixed shape (e.g.,
                liquids, gases, grain, liquid concrete, sand, gravel,
                aggregates) transported in a tank, hopper, box or similar device
                that forms part of the structure of a commercial motor vehicle.
              </p>
            </div>
          </div>

          {/* Performance Criteria */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-4951">
            <h2 className="text-3xl font-bold mb-6 flex items-center" data-id="element-4952">
              <Shield className="mr-3" size={32} data-id="element-4953" />
              Performance Criteria
            </h2>

            <p className="text-lg mb-6" data-id="element-4954">
              FMCSA requires that cargo securement systems be capable of
              withstanding the forces associated with the following three
              deceleration/accelerations, applied separately:
            </p>

            <div className="space-y-4" data-id="element-4955">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-4956">
                <p className="text-2xl font-bold mb-2" data-id="element-4957">0.8 g</p>
                <p className="text-gray-200" data-id="element-4958">
                  Deceleration in the forward direction
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-4959">
                <p className="text-2xl font-bold mb-2" data-id="element-4960">0.5 g</p>
                <p className="text-gray-200" data-id="element-4961">
                  Acceleration in the rearward direction
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-4962">
                <p className="text-2xl font-bold mb-2" data-id="element-4963">0.5 g</p>
                <p className="text-gray-200" data-id="element-4964">
                  Acceleration in a lateral direction
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 text-gray-900 rounded-lg p-6 mt-6" data-id="element-4965">
              <p className="font-semibold" data-id="element-4966">
                Motor carriers are NOT required to conduct testing of cargo
                securement systems. Cargo immobilized or secured in accordance
                with the general or commodity-specific rules are considered to
                meet the performance criteria.
              </p>
            </div>
          </div>

          {/* Key Highlights */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4967">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4968">
              Key Highlights of the Regulations
            </h2>

            <div className="space-y-6" data-id="element-4969">
              <div className="border-l-4 border-blue-500 pl-6" data-id="element-4970">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-4971">
                  Securement Devices
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-4972">
                  All devices and systems used for securing cargo must meet
                  performance criteria. FMCSA has updated references to
                  manufacturing standards for various types of tiedowns.
                </p>
                <div className="bg-blue-50 rounded p-4" data-id="element-4973">
                  <p className="text-gray-800" data-id="element-4974">
                    <strong data-id="element-4975">Important:</strong> Existing cargo securement
                    devices compliant with old regulations are deemed
                    satisfactory under new rules. Motor carriers do NOT need to
                    purchase new equipment solely for compliance.
                  </p>
                </div>
              </div>

              <div className="border-l-4 border-green-500 pl-6" data-id="element-4976">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-4977">
                  Proper Use and Placement of Tiedowns
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-4978">
                  <li className="flex items-start" data-id="element-4979">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-4980" />
                    <span data-id="element-4981">Tiedowns must remain secure during transit</span>
                  </li>
                  <li className="flex items-start" data-id="element-4982">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-4983" />
                    <span data-id="element-4984">
                      Position tiedowns inboard of rub rails when feasible
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-4985">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-4986" />
                    <span data-id="element-4987">
                      Use edge protection to prevent damage from sharp cargo
                      edges
                    </span>
                  </li>
                </ul>
              </div>

              <div className="border-l-4 border-yellow-500 pl-6" data-id="element-4988">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-4989">
                  Unmarked Tiedowns
                </h3>
                <p className="text-gray-700" data-id="element-4990">
                  No prohibition on using unmarked tiedowns, but certain
                  assumptions about working load limits apply. Unmarked welded
                  steel chain is considered to have working load limit equal to
                  grade 30 proof coil.
                </p>
              </div>
            </div>
          </div>

          {/* General Securement Rules */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4991">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4992">
              General Securement and Placement Rules
            </h2>

            <div className="space-y-6" data-id="element-4993">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-4994">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-4995">General Rule</h3>
                <p className="text-gray-700" data-id="element-4996">
                  Cargo must be firmly immobilized or secured on or within a
                  vehicle by structures of adequate strength, dunnage, dunnage
                  bags, shoring bars, tiedowns or a combination of these.
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-4997">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-4998">
                  Cargo Placement and Restraint
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-4999">
                  <li data-id="element-5000">
                    • Articles likely to roll must be restrained by chocks,
                    wedges, cradle or equivalent means
                  </li>
                  <li data-id="element-5001">
                    • Articles placed beside each other must be in direct
                    contact OR prevented from shifting towards each other
                  </li>
                </ul>
              </div>

              <div className="bg-purple-50 rounded-lg p-6" data-id="element-5002">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5003">
                  Minimum Working Load Limit
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-5004">
                  The aggregate working load limit of any securement system must
                  be <strong data-id="element-5005">at least one-half the weight</strong> of the
                  article or group of articles.
                </p>
              </div>
            </div>
          </div>

          {/* Minimum Number of Tiedowns */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5006">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5007">
              Minimum Number of Tiedowns
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-5008">
              When an article of cargo is NOT blocked or positioned to prevent
              movement in the forward direction:
            </p>

            <div className="space-y-4" data-id="element-5009">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4" data-id="element-5010">
                <p className="font-bold text-gray-900 mb-2" data-id="element-5011">
                  1 Tiedown Required:
                </p>
                <p className="text-gray-700" data-id="element-5012">
                  Articles 5 ft or less in length AND 1,100 lbs or less in
                  weight
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-4" data-id="element-5013">
                <p className="font-bold text-gray-900 mb-2" data-id="element-5014">
                  2 Tiedowns Required:
                </p>
                <ul className="text-gray-700 space-y-1" data-id="element-5015">
                  <li data-id="element-5016">
                    • 5 ft or less in length and MORE than 1,100 lbs in weight,
                    OR
                  </li>
                  <li data-id="element-5017">
                    • Greater than 5 ft but less than 10 ft (regardless of
                    weight)
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4" data-id="element-5018">
                <p className="font-bold text-gray-900 mb-2" data-id="element-5019">
                  Longer than 10 ft:
                </p>
                <p className="text-gray-700" data-id="element-5020">
                  Two tiedowns for first 10 ft + one additional tiedown for
                  every 10 ft (or fraction thereof) beyond first 10 ft
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-4" data-id="element-5021">
                <p className="font-bold text-gray-900 mb-2" data-id="element-5022">
                  If Blocked/Braced:
                </p>
                <p className="text-gray-700" data-id="element-5023">
                  At least one tiedown for every 10 ft of article length (or
                  fraction thereof)
                </p>
              </div>
            </div>
          </div>

          {/* Commodity-Specific Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5024">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-5025">
              <Package className="mr-3 text-[#B1050B]" size={28} data-id="element-5026" />
              Commodity-Specific Securement Requirements
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-5027">
              FMCSA has adopted detailed requirements for the securement of the
              following commodities. These commodity-specific guidelines take
              precedence over the general rules when applicable:
            </p>

            <div className="grid md:grid-cols-2 gap-4" data-id="element-5028">
              <div className="bg-blue-50 rounded-lg p-4" data-id="element-5029">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5030">
                  §393.116 - Logs
                </h3>
                <p className="text-sm text-gray-700" data-id="element-5031">
                  Transportation of logs with specific exceptions for unitized
                  logs and firewood
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-4" data-id="element-5032">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5033">
                  §393.118 - Dressed Lumber
                </h3>
                <p className="text-sm text-gray-700" data-id="element-5034">
                  Bundles of dressed lumber, packaged lumber, and similar
                  building products
                </p>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4" data-id="element-5035">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5036">
                  §393.120 - Metal Coils
                </h3>
                <p className="text-sm text-gray-700" data-id="element-5037">
                  Metal coils weighing 5,000 lbs or more (individually or
                  grouped)
                </p>
              </div>

              <div className="bg-purple-50 rounded-lg p-4" data-id="element-5038">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5039">
                  §393.122 - Paper Rolls
                </h3>
                <p className="text-sm text-gray-700" data-id="element-5040">
                  Paper rolls weighing 5,000 lbs or more (individually or
                  together)
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-4" data-id="element-5041">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5042">
                  §393.124 - Concrete Pipe
                </h3>
                <p className="text-sm text-gray-700" data-id="element-5043">
                  Concrete pipe on flatbed and lowboy trailers
                </p>
              </div>

              <div className="bg-indigo-50 rounded-lg p-4" data-id="element-5044">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5045">
                  §393.126 - Intermodal Containers
                </h3>
                <p className="text-sm text-gray-700" data-id="element-5046">
                  Freight containers for interchangeable multi-mode
                  transportation
                </p>
              </div>

              <div className="bg-pink-50 rounded-lg p-4" data-id="element-5047">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5048">
                  §393.128 - Automobiles
                </h3>
                <p className="text-sm text-gray-700" data-id="element-5049">
                  Automobiles, light trucks, and vans weighing 10,000 lbs or
                  less
                </p>
              </div>

              <div className="bg-orange-50 rounded-lg p-4" data-id="element-5050">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5051">
                  §393.130 - Heavy Vehicles
                </h3>
                <p className="text-sm text-gray-700" data-id="element-5052">
                  Heavy vehicles, equipment and machinery weighing 10,000 lbs or
                  more
                </p>
              </div>

              <div className="bg-teal-50 rounded-lg p-4" data-id="element-5053">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5054">
                  §393.132 - Flattened Vehicles
                </h3>
                <p className="text-sm text-gray-700" data-id="element-5055">
                  Vehicles that have been intentionally flattened or crushed
                </p>
              </div>

              <div className="bg-cyan-50 rounded-lg p-4" data-id="element-5056">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5057">
                  §393.134 - Roll-on/Roll-off
                </h3>
                <p className="text-sm text-gray-700" data-id="element-5058">
                  Roll-on/roll-off or hook-lift containers
                </p>
              </div>

              <div className="bg-lime-50 rounded-lg p-4" data-id="element-5059">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5060">
                  §393.136 - Large Boulders
                </h3>
                <p className="text-sm text-gray-700" data-id="element-5061">
                  Natural, irregularly shaped rock exceeding 11,000 lbs or 2
                  cubic meters
                </p>
              </div>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-5062">
            <h2 className="text-3xl font-bold mb-6" data-id="element-5063">Key Takeaways</h2>

            <div className="space-y-3" data-id="element-5064">
              <div className="flex items-start" data-id="element-5065">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5066" />
                <span data-id="element-5067">
                  Rules effective January 1, 2004, based on North American
                  standards
                </span>
              </div>
              <div className="flex items-start" data-id="element-5068">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5069" />
                <span data-id="element-5070">
                  Performance criteria: 0.8g forward, 0.5g rearward, 0.5g
                  lateral
                </span>
              </div>
              <div className="flex items-start" data-id="element-5071">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5072" />
                <span data-id="element-5073">
                  Existing compliant equipment does NOT need replacement
                </span>
              </div>
              <div className="flex items-start" data-id="element-5074">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5075" />
                <span data-id="element-5076">
                  Minimum working load limit: at least one-half cargo weight
                </span>
              </div>
              <div className="flex items-start" data-id="element-5077">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5078" />
                <span data-id="element-5079">
                  Tiedown requirements based on cargo length and weight
                </span>
              </div>
              <div className="flex items-start" data-id="element-5080">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5081" />
                <span data-id="element-5082">11 commodity-specific securement requirements</span>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border-2 border-yellow-500 rounded-lg p-6 mb-8" data-id="element-5083">
            <div className="flex items-start" data-id="element-5084">
              <AlertTriangle className="text-yellow-600 mr-3 flex-shrink-0 mt-1" size={24} data-id="element-5085" />
              <div data-id="element-5086">
                <p className="text-sm text-gray-800" data-id="element-5087">
                  <strong data-id="element-5088">Disclaimer:</strong> This content is for informational
                  purposes only. The information on this page is not legal
                  advice and does not portend to be the official rules. We do
                  our best to provide accurate information, but rules,
                  regulations and laws change so it's important that you confirm
                  the information for yourself at the official FMCSA website.
                </p>
              </div>
            </div>
          </div>

          {/* Official Resources */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5089">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5090">
              Official Resources
            </h2>

            <div className="space-y-3" data-id="element-5091">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-5092">
                <p className="font-semibold text-gray-900 mb-1" data-id="element-5093">
                  Federal Motor Carrier Safety Administration
                </p>
                <p className="text-gray-700 text-sm" data-id="element-5094">
                  Vehicle and Roadside Operations Division (MC-PSV)
                </p>
                <p className="text-gray-700 text-sm" data-id="element-5095">
                  1200 New Jersey Avenue SE
                </p>
                <p className="text-gray-700 text-sm" data-id="element-5096">Washington, DC 20590</p>
                <a href="https://www.fmcsa.dot.gov" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm" data-id="element-5097">
                  www.fmcsa.dot.gov
                </a>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-5098">
                <p className="font-semibold text-gray-900 mb-1" data-id="element-5099">
                  Publication Information
                </p>
                <p className="text-gray-700 text-sm" data-id="element-5100">
                  Publication No.: MC-P/PSV-04-001
                </p>
                <p className="text-gray-700 text-sm" data-id="element-5101">
                  Last updated: Monday, March 3, 2014
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-5102">
            <h2 className="text-3xl font-bold mb-4" data-id="element-5103">
              Need Help with Cargo Securement Compliance?
            </h2>
            <p className="text-xl mb-6" data-id="element-5104">
              Expert guidance on FMCSA cargo securement regulations
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-5105">
              <Phone className="mr-3" size={24} data-id="element-5106" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-5107">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}